import React, { useRef, useState } from 'react'
import { Grid, Typography, useTheme } from '@mui/material'
import { styled } from '@mui/styles'
import * as R from 'ramda'

import { getGridLines } from '~/components/dashboard/clients/snapshots-and-records/snapshots/VitalsChart/utils'
import type { VitalChartItem } from '~/types'
import useWindowResize from '~/utils/useWindowResize'

interface RhapVitalsChartProps {
  data: VitalChartItem[]
}

const StyledOuterGrid = styled(Grid)({
  display: 'flex',
  justifyContent: 'center',
  overflowX: 'auto',
  overflowY: 'hidden',
  position: 'relative',
})

const StyledScrollingContainer = styled(Grid)({
  position: 'absolute',
  left: 0,
  top: 0,
})

const StyledDateLabelGrid = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(0.5, 0),
  backgroundColor: theme.colors.grayGray4,
}))

const StyledValueLabelsGrid = styled(Grid)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
})

const StyledDateLabel = styled(Typography)(({ theme }) => ({
  flex: 'auto',
  fontWeight: '500',
  textAlign: 'center',
  fontSize: theme.typography.pxToRem(14),
  color: theme.colors.grayGray1,
}))

const StyledDisplayValue = styled(Typography)(({ theme }) => ({
  flex: 'auto',
  paddingBottom: theme.spacing(1.5),
  paddingTop: theme.spacing(2.5),
  textAlign: 'center',
  fontSize: theme.typography.pxToRem(14),
  color: theme.colors.grayGray1,
}))

const RhapVitalsChart = ({ data }: RhapVitalsChartProps) => {
  const theme = useTheme()
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  // Chart configuration
  const chartHeight = 120
  const legendHeight = 16.5
  const scrollHeight = 70
  const minDataPointSectionWidth = 70
  const paddingTop = 8
  const paddingBottom = 30
  const gridLinesCount = 3

  // Calculate responsive chart width
  const chartWidth = Math.max(
    Math.max(dimensions.width, minDataPointSectionWidth * data.length) - 1,
    0,
  )

  // Handle container resizing
  const updateContainerSize = () => {
    if (containerRef.current) {
      setDimensions({
        width: containerRef.current.offsetWidth,
        height: containerRef.current.offsetHeight,
      })
    }
  }
  useWindowResize(updateContainerSize)

  // Extract numeric values for plotting
  const values = R.pluck('value', data)
  const reverseY = (y: number) => chartHeight - y

  // Calculate grid line positions
  const getLines = (count: number) => new Array(count).fill(undefined)

  const stepHeight =
    (chartHeight - paddingTop - paddingBottom) / (gridLinesCount - 1)

  const gridLinesCoordinates = getLines(gridLinesCount).map((_, index) => {
    const y = reverseY(paddingBottom + index * stepHeight)
    const x1 = 0
    const y1 = y
    const x2 = chartWidth
    const y2 = y

    return `M ${x1} ${y1} L${x2} ${y2}`
  })

  // Calculate data point positions
  const gridLines = getGridLines(values, gridLinesCount)
  const pointsDelta =
    (chartHeight - (paddingTop + paddingBottom)) /
    (gridLines[gridLinesCount - 1] - gridLines[0])

  const points = values.map((value, index) => {
    const y = paddingBottom + (value - gridLines[0]) * pointsDelta
    const sectionWidth = chartWidth / values.length
    const x = sectionWidth * index + sectionWidth / 2
    return {
      x,
      y: reverseY(y),
      r: 6,
    }
  })

  // Generate SVG path for connecting line
  const path = points.reduce(
    (acc, { x, y }, index) => `${acc}${index === 0 ? 'M' : 'L'}${x} ${y}`,
    '',
  )

  return (
    <StyledOuterGrid
      data-testid="Vitals chart"
      height={chartHeight + legendHeight + scrollHeight}
      ref={containerRef}
    >
      <StyledScrollingContainer width={chartWidth}>
        {/* Date labels */}
        <StyledDateLabelGrid>
          {data.map(({ label }, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <StyledDateLabel key={index}>{label}</StyledDateLabel>
          ))}
        </StyledDateLabelGrid>

        {/* Value labels with units */}
        <StyledValueLabelsGrid>
          {data.map(({ displayValue }, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <StyledDisplayValue key={index}>{displayValue}</StyledDisplayValue>
          ))}
        </StyledValueLabelsGrid>

        {/* SVG Chart */}
        <svg height={chartHeight} width={chartWidth}>
          {/* Grid lines */}
          {gridLinesCoordinates.map((lineCoordinates, index) => (
            <path
              d={lineCoordinates}
              fill="none"
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              stroke={theme.colors.grayGray3}
              strokeWidth="0.5"
            />
          ))}

          {/* Connecting line between data points */}
          <path
            d={path}
            fill="none"
            stroke={theme.colors.brandPrimary}
            strokeWidth="2"
          />

          {/* Data point circles */}
          {points.map(({ x, y, r }, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <g key={index}>
              <circle
                cx={x}
                cy={y}
                data-testid="vital-circle-point"
                fill={theme.colors.brandPrimary}
                r={r}
                stroke={theme.colors.brandPrimary}
                strokeWidth="2"
              />
            </g>
          ))}
        </svg>
      </StyledScrollingContainer>
    </StyledOuterGrid>
  )
}

export default RhapVitalsChart
